import { debugLog } from '../core/utils/logger';
import { SigningAdapter, type SignL1ActionParams } from './base-adapter';
import { EIP712_DOMAINS, EIP712_TYPES } from '../core/constants';
import type { PrivySignTypedDataFunction } from '../types/privy';

export interface PrivyHookAdapterConfig {
  signTypedData: PrivySignTypedDataFunction;
  walletAddress: string;
  showWalletUIs?: boolean;
}

/**
 * Signing adapter for React Web and React Native using Privy's useSignTypedData hook
 */
export class PrivyHookAdapter extends SigningAdapter {
  private config: PrivyHookAdapterConfig;

  constructor(config: PrivyHookAdapterConfig) {
    super();
    this.config = config;
  }

  async signL1Action(params: SignL1ActionParams): Promise<`0x${string}`> {
    // Privy's signTypedData needs EIP712Domain in types (like viem does)
    // even though it follows ethers pattern for the function signature
    const eip712Message = {
      domain: EIP712_DOMAINS.EXCHANGE,
      primaryType: 'Agent',
      types: {
        EIP712Domain: [
          { name: 'name', type: 'string' },
          { name: 'version', type: 'string' },
          { name: 'chainId', type: 'uint256' },
          { name: 'verifyingContract', type: 'address' },
        ],
        ...EIP712_TYPES.AGENT,
      },
      message: {
        source: params.isTestnet ? 'b' : 'a',
        connectionId: params.actionHash,
      },
    };

    const signOptions = {
      uiOptions: { showWalletUIs: this.config.showWalletUIs ?? false },
      address: this.config.walletAddress,
    };

    debugLog('[Privy Adapter] Signing EIP-712 message:', JSON.stringify(eip712Message, null, 2));
    debugLog('[Privy Adapter] Sign options:', JSON.stringify(signOptions, null, 2));

    const { signature } = await this.config.signTypedData(eip712Message, signOptions);

    debugLog('[Privy Adapter] Received signature:', signature);

    return signature as `0x${string}`;
  }

  async isReady(): Promise<boolean> {
    return !!this.config.signTypedData && !!this.config.walletAddress;
  }

  getWalletAddress(): string {
    return this.config.walletAddress;
  }
}
